import type { TahitiEnvironmentValue } from '@mss_creator/shared';
import { ActionBar, Alert, Button, Dialog, DialogClose } from '@pms-digital/design-system';
import { useRef } from 'react';

const TARGET_NAMES: Record<TahitiEnvironmentValue, string> = { prod: 'Prod', dev: 'Dev' };

interface TahitiConfirmDialogProps {
  /** The environment to switch to, or `null` while the dialog is closed. */
  target: TahitiEnvironmentValue | null;
  /** True when moving back to the default (Prod): the wording says "Repasser". */
  backToDefault: boolean;
  pending: boolean;
  failed: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation before switching the Tahiti login server from the settings page:
 * the change signs every user out, the current one included. Focus lands on
 * "Annuler" so a stray Enter signs nobody out. Nothing is applied until the
 * confirm button is pressed; a failure leaves the dialog open with the reason.
 */
export function TahitiConfirmDialog({
  target,
  backToDefault,
  pending,
  failed,
  onConfirm,
  onCancel,
}: TahitiConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const name = target ? TARGET_NAMES[target] : '';
  const verb = backToDefault ? 'Repasser' : 'Passer';

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open && !pending) onCancel();
      }}
      tone="destructive"
      title={`${verb} le serveur de connexion en ${name} ?`}
      description="Tous les utilisateurs seront déconnectés, vous compris."
      initialFocus={cancelRef}
      actions={
        <ActionBar
          align="center"
          secondary={
            <DialogClose asChild>
              <Button ref={cancelRef} variant="ghost" disabled={pending}>
                Annuler
              </Button>
            </DialogClose>
          }
          primary={
            <Button
              variant="destructive"
              loading={pending}
              loadingLabel="Changement en cours…"
              onClick={onConfirm}
            >
              {`${verb} en ${name}`}
            </Button>
          }
        />
      }
    >
      <p className="text-center">
        {`Vous serez ensuite renvoyé vers la page de connexion, qui vérifiera les identifiants auprès de Tahiti ${name}.`}
      </p>
      {failed && (
        <div className="mt-4 text-left">
          <Alert tone="danger">
            Le changement a échoué. Rien n'a été modifié et personne n'a été déconnecté.
          </Alert>
        </div>
      )}
    </Dialog>
  );
}
